/**
 * TermsOfServicePage.jsx
 * 
 * Full terms of service page for Touchstone.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

export default function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/home"
            className="text-xl text-white"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
          >
            TOUCHSTONE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif" }}
          >
            Terms of Service
          </h1>
          <p className="text-sm text-zinc-500 mb-16">Last updated: February 16, 2026</p>

          <div className="space-y-12 text-zinc-400 leading-relaxed">

            <section>
              <h2 className="text-xl text-white font-medium mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing or using the Touchstone platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, you may not access or use the Service. We reserve the right to update 
                these Terms at any time, and your continued use constitutes acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">2. Description of Service</h2>
              <p>
                Touchstone is a neutral, independent research platform that documents real-world AI tool usage. 
                Our Service provides tool comparisons, capability profiles, workflow building tools, and educational 
                blog content. Touchstone does not rank tools, accept affiliate commissions, or publish sponsored content. 
                All information is provided for research and informational purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">3. Use of the Service</h2>
              <p className="mb-4">By using Touchstone, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Use the Service only for lawful purposes and in accordance with these Terms</li>
                <li>Not attempt to interfere with, compromise, or disrupt the Service or its infrastructure</li>
                <li>Not use automated systems (bots, scrapers) to access the Service without our express written permission</li>
                <li>Not reproduce, distribute, or create derivative works from our content without authorization</li>
                <li>Not misrepresent your affiliation with any person or entity when using the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">4. Intellectual Property</h2>
              <p className="mb-4">
                All content on Touchstone — including text, graphics, logos, research data, blog articles, UI design, 
                and software — is the property of Touchstone or its content suppliers and is protected by intellectual 
                property laws.
              </p>
              <p>
                The Touchstone name, logo, and all related marks are trademarks of Touchstone. You may not use 
                these marks without our prior written consent. Tool names, logos, and trademarks referenced on 
                the platform belong to their respective owners and are used for identification purposes only.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">5. User-Generated Data</h2>
              <p>
                Workflows, profiles, and preferences you create are stored locally in your browser. 
                You retain ownership of any configurations you create. We do not claim rights over 
                user-generated configurations. You are responsible for maintaining backups of your 
                locally stored data.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">6. Disclaimer of Warranties</h2>
              <p className="mb-4">
                The Service is provided on an <span className="text-white">"as is"</span> and <span className="text-white">"as available"</span> basis 
                without warranties of any kind, either express or implied.
              </p>
              <p className="mb-4">Touchstone does not warrant that:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                <li>The information, tool data, or comparisons provided are complete, accurate, or current</li>
                <li>Any particular AI tool will perform as described — our data reflects documented usage patterns, not guarantees</li>
                <li>The results of using the Service will meet your specific requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">7. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Touchstone and its operators shall not be liable for any 
                indirect, incidental, special, consequential, or punitive damages, including but not limited to 
                loss of profits, data, or use, arising out of or related to your use of the Service. 
                Our total liability for any claim arising from these Terms shall not exceed the amount 
                you paid us in the twelve (12) months preceding the claim, if any.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">8. Third-Party Links & Tools</h2>
              <p>
                Touchstone may contain links to third-party websites and references to third-party AI tools and services. 
                These links and references are provided for informational purposes only. We do not endorse, control, 
                or assume responsibility for the content, privacy policies, or practices of any third-party sites or services. 
                Your interactions with third-party services are governed by their own terms and policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">9. Neutrality Statement</h2>
              <p>
                Touchstone is committed to neutrality. We do not accept payment, affiliate commissions, 
                or other compensation from any AI tool vendor in exchange for favorable coverage or placement. 
                Our research and comparisons are based on documented usage patterns and publicly available information. 
                If you believe any content on our platform is biased or inaccurate, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">10. Termination</h2>
              <p>
                We reserve the right to suspend or terminate access to the Service at our sole discretion, 
                without notice, for conduct that we determine violates these Terms or is harmful to other 
                users, us, or third parties, or for any other reason.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">11. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with applicable law, 
                without regard to conflict of law principles. Any disputes arising under these Terms 
                shall be resolved in the appropriate courts of competent jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">12. Contact Us</h2>
              <p>
                If you have questions about these Terms of Service, please contact us at{' '}
                <a href="mailto:legal@touchstone.dev" className="text-white hover:underline">
                  legal@touchstone.dev
                </a>.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
